// Date formatting utilities for consistent display across the frontend

/**
 * Format a date to DD/MM/YY format
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date as DD/MM/YY
 */
export const formatDateDDMMYY = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    
    return `${day}/${month}/${year}`;
};

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const toISTDate = (date) => {
    if (!date) return null;
    let d;
    if (typeof date === 'string') {
        // Only append Z when string has no timezone info (no Z, no +HH:MM, no -HH:MM)
        const hasTimezone = /Z|[+-]\d{2}:\d{2}$/.test(date);
        d = new Date(hasTimezone ? date : date + 'Z');
    } else {
        d = new Date(date);
    }
    if (isNaN(d.getTime())) return null;
    const istOffset = 5.5 * 60 * 60 * 1000;
    return new Date(d.getTime() + istOffset);
};

/**
 * Format a date to DD/MMM/YYYY, hh:mm AM/PM (IST)
 * @param {string|Date} date - Date string or Date object (assumes UTC from server)
 * @returns {string} Formatted date and time in IST, e.g. "14/May/2026, 09:35 AM"
 */
export const formatDateTimeIST = (date) => {
    const istDate = toISTDate(date);
    if (!istDate) return '-';

    const day = String(istDate.getUTCDate()).padStart(2, '0');
    const month = MONTH_ABBR[istDate.getUTCMonth()];
    const year = istDate.getUTCFullYear();
    const hours24 = istDate.getUTCHours();
    const minutes = String(istDate.getUTCMinutes()).padStart(2, '0');
    const hours12 = hours24 % 12 || 12;
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    const hoursStr = String(hours12).padStart(2, '0');

    return `${day}/${month}/${year}, ${hoursStr}:${minutes} ${ampm}`;
};

/**
 * Format a date to DD/MMM/YYYY (IST, date only)
 * @param {string|Date} date - Date string or Date object (assumes UTC from server)
 * @returns {string} Formatted date in IST, e.g. "14/May/2026"
 */
export const formatISTDateOnly = (date) => {
    const istDate = toISTDate(date);
    if (!istDate) return '-';

    const day = String(istDate.getUTCDate()).padStart(2, '0');
    const month = MONTH_ABBR[istDate.getUTCMonth()];
    const year = istDate.getUTCFullYear();

    return `${day}/${month}/${year}`;
};

/**
 * Format a date to full date-time string (DD/MM/YY HH:MM:SS)
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date and time
 */
export const formatFullDateTime = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};
